import { on } from "@ngrx/store";
import { initialCustomerState, CustomerState } from "./customer.state";
import { CustomerActions } from "./customer.actions";
import { QuoteActions } from "../quote/quote.actions";
import { customersReducer } from "./customers/customers.reducer";
import { PagesStore } from "../pages.state";

// **************************************************************************
// HELPER
// **************************************************************************

function withCustomer(state: PagesStore, updater: (customer: CustomerState) => CustomerState): PagesStore {
    return { ...state, customer: updater(state.customer) };
}

// **************************************************************************
// CUSTOMER REDUCER (array format for PagesStore)
// **************************************************************************

export const customerReducer = [
    // ACTIONS REDUCER
    on(
        CustomerActions.initializeCustomer,
        CustomerActions.updateCustomer,
        CustomerActions.removeCustomer,
        (state: PagesStore) => withCustomer(state, customerState => ({
            ...customerState,
            error: null,
        })),
    ),

    on(
        CustomerActions.initializeCustomerSuccess,
        (state: PagesStore, { customer }) => withCustomer(state, customerState => ({
            ...customerState,
            customer,
            error: null,
        })),
    ),

    on(CustomerActions.updateCustomerSuccess, (state: PagesStore, { customer }) => withCustomer(state, customerState => ({
        ...customerState,
        customer,
        error: null,
    }))),

    on(
        CustomerActions.removeCustomerSuccess,
        (state: PagesStore) => withCustomer(state, () => ({
            ...initialCustomerState,
            error: null,
        })),
    ),

    on(
        CustomerActions.initializeCustomerFailure,
        CustomerActions.removeCustomerFailure,
        (state: PagesStore, { error }) => withCustomer(state, customerState => ({
            ...customerState,
            error,
        })),
    ),

    // **************************************************************************
    // DELIVERY ADDRESS - Add new address when created via Quote or Customer
    // **************************************************************************
    on(QuoteActions.createDeliveryAddressSuccess, (state: PagesStore, { address }) => withCustomer(state, customerState => ({
        ...customerState,
        customer: customerState.customer ? {
            ...customerState.customer,
            deliveryAddresses: [address, ...customerState.customer.deliveryAddresses],
        } : null,
    }))),

    on(CustomerActions.createDeliveryAddressSuccess, (state: PagesStore, { address }) => withCustomer(state, customerState => ({
        ...customerState,
        customer: customerState.customer ? {
            ...customerState.customer,
            deliveryAddresses: [address, ...customerState.customer.deliveryAddresses],
        } : null,
        creatingDeliveryAddress: false,
    }))),

    on(CustomerActions.createDeliveryAddress, (state: PagesStore) => withCustomer(state, customerState => ({
        ...customerState,
        creatingDeliveryAddress: true,
    }))),

    on(CustomerActions.createDeliveryAddressFailure, (state: PagesStore, { error }) => withCustomer(state, customerState => ({
        ...customerState,
        creatingDeliveryAddress: false,
        error,
    }))),

    // **************************************************************************
    // SEARCH HANDLERS
    // **************************************************************************
    on(CustomerActions.searchCustomersSuccess, (state: PagesStore, { results }) => withCustomer(state, customerState => ({
        ...customerState,
        customerSearch: {
            ...customerState.customerSearch,
            results,
        },
    }))),

    on(CustomerActions.searchCustomersFailure, (state: PagesStore, { error }) => withCustomer(state, customerState => ({
        ...customerState,
        error,
    }))),

    on(CustomerActions.selectCustomer, (state: PagesStore, { customer }) => withCustomer(state, customerState => ({
        ...customerState,
        customerSearch: {
            ...customerState.customerSearch,
            selected: customer,
        },
    }))),

    on(CustomerActions.resetCustomerSearch, (state: PagesStore) => withCustomer(state, customerState => ({
        ...customerState,
        customerSearch: {
            results: [],
            selected: undefined,
        },
    }))),

    // **************************************************************************
    // LOADING HANDLERS
    // **************************************************************************

    // Customer loading
    on(CustomerActions.initializeCustomer, CustomerActions.updateCustomer, (state: PagesStore) => withCustomer(state, customerState => ({
        ...customerState,
        loadingDetail: true,
    }))),

    on(CustomerActions.createCustomer, (state: PagesStore) => withCustomer(state, customerState => ({
        ...customerState,
        creating: true,
    }))),

    on(
        CustomerActions.initializeCustomerSuccess,
        CustomerActions.initializeCustomerFailure,
        CustomerActions.updateCustomerSuccess,
        CustomerActions.updateCustomerFailure,
        CustomerActions.removeCustomerFailure,
        CustomerActions.createCustomerSuccess,
        CustomerActions.createCustomerFailure,
        (state: PagesStore) => withCustomer(state, customerState => ({
            ...customerState,
            loadingDetail: false,
            creating: false,
        })),
    ),

    // Customer search loading
    on(CustomerActions.searchCustomers, (state: PagesStore) => withCustomer(state, customerState => ({
        ...customerState,
        searchingCustomer: true,
    }))),

    on(CustomerActions.searchCustomersSuccess, CustomerActions.searchCustomersFailure, (state: PagesStore) => withCustomer(state, customerState => ({
        ...customerState,
        searchingCustomer: false,
    }))),

    // **************************************************************************
    // ATTIO SYNC HANDLERS
    // **************************************************************************
    on(CustomerActions.syncFromAttio, (state: PagesStore) => withCustomer(state, customerState => ({
        ...customerState,
        syncingFromAttio: true,
    }))),

    on(CustomerActions.syncFromAttioSuccess, CustomerActions.syncFromAttioFailure, (state: PagesStore) => withCustomer(state, customerState => ({
        ...customerState,
        syncingFromAttio: false,
    }))),

    // **************************************************************************
    // CUSTOMERS LIST HANDLERS
    // **************************************************************************
    ...customersReducer,
];
